import { DateTime } from 'luxon';
import { DateTimeInput, ObjectDate } from '../components/organisms/form';
import { AvailableDays } from '../components/organisms/form/TimeFrameGrid';
import { DayType } from '../graphql/generated';

export const getRequiredEnvironmentVariable = (envName: string): string => {
  const res = process.env[envName];
  if (res == null || res === '') {
    throw new Error(`environment ${envName} is not found.`);
  }

  return res;
};

export function qs(url: string): { [key: string]: string } {
  return url
    .substring(1)
    .split('&')
    .map((p) => p.split('='))
    .reduce((obj, e) => ({ ...obj, [e[0]]: e[1] }), {});
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

// 2021年4月5日
export function formatJstDateJapanese(isoString?: string): string {
  if (!isoString) {
    return '';
  }
  return DateTime.fromISO(isoString).toFormat('yyyy年MM月dd日');
}

// 2021年4月5日 23:45
export function formatJstDateTimeJapanese(isoString?: string): string {
  if (!isoString) {
    return '';
  }
  return DateTime.fromISO(isoString).toFormat('yyyy年MM月dd日 HH:mm');
}

// 2021年4月5日 23:45:56
export function formatJstDateTimeSecondJapanese(isoString?: string): string {
  if (!isoString) {
    return '';
  }
  return DateTime.fromISO(isoString).toFormat('yyyy年MM月dd日 HH:mm:ss');
}

// 2021/4/5
export function formatJstDate(isoString?: string): string {
  if (!isoString) {
    return '';
  }
  return DateTime.fromISO(isoString).toFormat('yyyy/MM/dd');
}

// 2021/4/5 23:45
export function formatJstDateTime(isoString?: string): string {
  if (!isoString) {
    return '';
  }
  return DateTime.fromISO(isoString).toFormat('yyyy/MM/dd HH:mm');
}

// 202104052345
export function formatJstDateTimeNumeric(isoString?: string): string {
  if (!isoString) {
    return '';
  }
  return DateTime.fromISO(isoString).toFormat('yyyyMMddHHmm');
}

export function formatNumberToJaJpLocale(num: number): string {
  return num.toLocaleString('ja');
}

// "?topic=webdev" => "?topic=webdev&key=value"
export function appendSearchParameter(base: string, kvs: { [key: string]: string }): string {
  const param = new URLSearchParams(base);
  Object.entries(kvs).forEach(([k, v]) => {
    param.append(k, v);
  });
  return param.toString();
}

/**
 * チェックデジットチェック
 * @param barcodeStr
 */
export function eanCheckDigit(barcodeStr: string): boolean {
  const barcode = ('00000' + barcodeStr).slice(-13);
  let evenNum = 0;
  let oddNum = 0;
  for (let i = 0; i < barcode.length - 1; i++) {
    if (i % 2 == 0) {
      oddNum += Number(barcode[i]);
    } else {
      evenNum += Number(barcode[i]);
    }
  }

  const checkNumber = Number((evenNum * 3 + oddNum).toString().slice(-1));
  const checkDigit = Number(barcode.slice(-1));

  // 下1桁が0の場合は、チェックディジットは0
  if (checkNumber === 0) {
    return checkNumber === checkDigit;
  }

  return 10 - checkNumber === checkDigit;
}

/**
 * フォーム入力値の日付をluxon.DateTimeに変換する
 * @param input
 */
export function fromInputDate(input?: DateTimeInput): DateTime | undefined {
  if (!input) {
    return undefined;
  }

  if (input instanceof DateTime) {
    return input;
  }

  return DateTime.fromMillis((input as ObjectDate).ts);
}

/**
 * フォーム日付＋時刻入力値の日付をluxon.DateTimeに変換する
 * @param inputDate 入力日付
 * @param inputTime 入力時刻
 */
export function fromInputDateAndTime(inputDate?: DateTimeInput, inputTime?: DateTimeInput): DateTime | undefined {
  const date = fromInputDate(inputDate)?.startOf('day');
  const time = fromInputDate(inputTime);
  if (!date || !time) {
    return undefined;
  }

  // 日付 + （時刻 = 今日の日付時刻 - 今日の始まり）
  return DateTime.fromMillis(date.toMillis() + (time.toMillis() - time.startOf('day').toMillis()));
}

/**
 * 2つの日付の逆転チェック
 */
export function validateStartEnd(startDateTime?: DateTime, endDateTime?: DateTime, validateSame?: boolean): boolean {
  // どちらかが空だったら成功
  if (!startDateTime || !endDateTime) {
    return true;
  }

  if (validateSame) return startDateTime <= endDateTime;

  return startDateTime < endDateTime;
}

/**
 * DateTime('2021-03-22T02:45:21.456+09:00') => '02:45'
 */
export function convertTimeFrameToDateString(input?: DateTimeInput): string | undefined {
  return fromInputDate(input)?.toFormat('HH:mm');
}

/**
 * '1,2, 31,3 => [1,2,31,3]
 */
export function convertTimeFrameToDateArray(input?: string): string[] {
  if (!input || input.length === 0) {
    return [];
  }

  return input.split(',').map((day) => day.trim());
}

/**
 * {
      mon: true,
      tue: true,
      wed: false,
      thu: false,
      fri: false,
      sat: false,
      sun: false,
    } => ['mon', 'tue']
 */
export function convertTimeFrameToDaysArray(input?: AvailableDays): DayType[] {
  if (!input) {
    return [];
  }

  return Object.entries(input).reduce<DayType[]>(
    (acc, [day, available]) => (available ? acc.concat([day as DayType]) : acc),
    []
  );
}
